import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Alert,
  Chip
} from '@mui/material';
import {  
  CheckCircle as ApprovedIcon,
  Pending as PendingIcon,
  Cancel as RejectedIcon
} from '@mui/icons-material'; 
import { colors } from '../../utils/colors';
import { getProjectProcurementsAPI } from '../../lib/api';
import LoadingProgress from '../../components/common/LoadingProgress';

const ProcurementCard = ({ procurement, onClick }) => {
  const formatAmount = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,  
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return {
          bg: colors.success.light,
          text: colors.success.main,
          icon: <ApprovedIcon sx={{ fontSize: 16 }} />
        };
      case 'pending':
        return {
          bg: colors.warning.light,
          text: colors.warning.main,
          icon: <PendingIcon sx={{ fontSize: 16 }} />
        };
      case 'rejected':
        return {
          bg: colors.error.light,
          text: colors.error.main,
          icon: <RejectedIcon sx={{ fontSize: 16 }} />
        };
      default:
        return {
          bg: colors.gray[100],
          text: colors.text.secondary,
          icon: null
        };
    }
  };

  const statusInfo = getStatusColor(procurement.status);

  return (
    <Card
      elevation={0}
      onClick={() => onClick(procurement)}
      sx={{
        height: '100%',
        borderRadius: { xs: 2, sm: 2.5, md: 3 },
        border: `1px solid ${colors.alpha.black08}`,
        bgcolor: colors.background.primary,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          borderColor: colors.primary.main,
          boxShadow: `0 4px 12px ${colors.primary.main}20`
        }
      }}
    >
      <CardContent sx={{ p: { xs: 1.75, sm: 2, md: 2.5 }, '&:last-child': { pb: { xs: 1.75, sm: 2, md: 2.5 } } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: { xs: 1.5, sm: 2 }, gap: 1 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: colors.text.primary,
              fontSize: { xs: '0.9375rem', sm: '1rem', md: '1.0625rem' },
              flex: 1,
              wordBreak: 'break-word',
              lineHeight: 1.3
            }}
          >
            {procurement.name}
          </Typography>
          <Chip
            icon={statusInfo.icon}
            label={procurement.status}
            size="small"
            sx={{
              bgcolor: statusInfo.bg,
              color: statusInfo.text,
              fontWeight: 600,
              textTransform: 'capitalize',
              fontSize: { xs: '0.6875rem', sm: '0.75rem' },
              height: { xs: 22, sm: 24 },
              flexShrink: 0,
              '& .MuiChip-icon': {
                color: statusInfo.text,
                fontSize: { xs: 14, sm: 16 }
              },
              '& .MuiChip-label': {
                px: { xs: 0.75, sm: 1 }
              }
            }}
          />
        </Box>
        <Box sx={{ mb: { xs: 1.25, sm: 1.5 } }}>
          <Typography
            variant="caption"
            sx={{
              color: colors.text.secondary,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.3px',
              fontSize: { xs: '0.625rem', sm: '0.6875rem' }
            }}
          >
            Category
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: colors.text.primary,
              fontWeight: 500,
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              mt: 0.25
            }}
          >
            {procurement.category}
          </Typography>
        </Box>

        <Box sx={{ mb: { xs: 1.25, sm: 1.5 } }}>
          <Typography
            variant="caption"
            sx={{
              color: colors.text.secondary,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.3px',
              fontSize: { xs: '0.625rem', sm: '0.6875rem' }
            }}
          >
            Sub Category
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: colors.text.primary,
              fontWeight: 500,
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              mt: 0.25
            }}
          >
            {procurement.sub_category}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pt: { xs: 1.25, sm: 1.5 },
            borderTop: `1px solid ${colors.alpha.black04}`,
            gap: 1
          }}
        >
          {procurement.quantity && (
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: colors.text.secondary,
                  fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                  fontWeight: 500
                }}
              >
                Qty: {procurement.quantity}
              </Typography>
            </Box>
          )}
          <Typography
            variant="h6"
            sx={{
              color: colors.primary.main,
              fontWeight: 700,
              fontSize: { xs: '1rem', sm: '1.0625rem', md: '1.125rem' },
              ml: 'auto'
            }}
          >
            {formatAmount(procurement.total_amount)}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};
 
const ProcurementList = ({ customerId, projectId, onProcurementClick }) => {
  const [procurements, setProcurements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchProcurements();
  }, [customerId, projectId]);

  const fetchProcurements = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await getProjectProcurementsAPI(customerId, projectId);
      if (result.status && result.data) {
        setProcurements(Array.isArray(result.data) ? result.data : []);
      } else {
        setError('Failed to load procurements');
      }
    } catch (error) { 
      setError(error.message || 'Failed to fetch procurements');
      console.error('Error fetching procurements:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProcurements = procurements.filter((procurement) => {
    if (filter === 'all') return true;
    return procurement.status?.toLowerCase() === filter;
  });

  const getStatusCount = (status) => {
    return procurements.filter(p => p.status?.toLowerCase() === status).length;
  };

  return (
    <>
      <LoadingProgress loading={loading} />
      <Box sx={{ p: 0 }}>
        <Box
          sx={{
            display: 'flex',
            gap: { xs: 1, sm: 1.25 },
            mb: { xs: 2.5, sm: 3 },
            flexWrap: 'wrap'
          }}
        >
          <Chip
            label={`All (${procurements.length})`}
            onClick={() => setFilter('all')}
            sx={{
              bgcolor: filter === 'all' ? colors.primary.main : colors.background.hover,
              color: filter === 'all' ? '#fff' : colors.text.secondary,
              fontWeight: 600,
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              height: { xs: 30, sm: 32 },
              px: { xs: 1, sm: 1.5 },
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: filter === 'all' ? colors.primary.dark : colors.gray[100],
                transform: 'scale(1.03)'
              }
            }}
          />
          <Chip
            label={`Approved (${getStatusCount('approved')})`}
            onClick={() => setFilter('approved')}
            sx={{
              bgcolor: filter === 'approved' ? colors.success.main : colors.background.hover,
              color: filter === 'approved' ? '#fff' : colors.text.secondary,
              fontWeight: 600,
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              height: { xs: 30, sm: 32 },
              px: { xs: 1, sm: 1.5 },
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: filter === 'approved' ? colors.success.dark : colors.gray[100],
                transform: 'scale(1.03)'
              }
            }}
          />
          <Chip
            label={`Pending (${getStatusCount('pending')})`}
            onClick={() => setFilter('pending')}
            sx={{
              bgcolor: filter === 'pending' ? colors.warning.main : colors.background.hover,
              color: filter === 'pending' ? '#fff' : colors.text.secondary,
              fontWeight: 600,
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              height: { xs: 30, sm: 32 },
              px: { xs: 1, sm: 1.5 },
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: filter === 'pending' ? colors.warning.dark : colors.gray[100],
                transform: 'scale(1.03)'
              }
            }}
          />
          <Chip
            label={`Rejected (${getStatusCount('rejected')})`}
            onClick={() => setFilter('rejected')}
            sx={{
              bgcolor: filter === 'rejected' ? colors.error.main : colors.background.hover,
              color: filter === 'rejected' ? '#fff' : colors.text.secondary,
              fontWeight: 600,
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              height: { xs: 30, sm: 32 },
              px: { xs: 1, sm: 1.5 },
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: filter === 'rejected' ? colors.error.dark : colors.gray[100],
                transform: 'scale(1.03)'
              }
            }}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {filteredProcurements.length === 0 && !loading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
              textAlign: 'center'
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: colors.text.tertiary,
                mb: 1,
                fontWeight: 600
              }}
            >
              
              No Procurements Found
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: colors.text.secondary
              }}
            >
              {filter === 'all'
                ? 'No procurements available for this project.'
                : `No ${filter} procurements found.`}
            </Typography>
          </Box>
        ) : (
          !loading && (
            <Grid container spacing={{ xs: 1.75, sm: 2, md: 2.5 }}>
              {filteredProcurements.map((procurement) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={6}
                  lg={4}
                  xl={3}
                  key={procurement.id}
                >
                  <ProcurementCard
                    procurement={procurement}
                    onClick={onProcurementClick}
                  />
                </Grid>
              ))}
            </Grid>
          )
        )}
      </Box>
    </>
  );
};

export default ProcurementList;